import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { flattenDeep } from 'lodash';
import { downloadCSVExport } from '@actions/data-table-actions';
import { openDashboardDialog } from '@actions/dashboard-actions';
import { BATCH_DATA_TYPE, GROUP_DATA_TYPE } from '@components/config/constants';
import { useCanAccessType, useTypeIsReadOnly } from '@components/config/permissions/hooks';
import { getDataTypeConfig } from '@components/config/selectors';
import { getDataTypeFilterParams } from '@components/filters/selectors';
import BatchUploadEntitiesDialog from '@components/portal/table/batch-upload-entities-dialog';
import BatchUploadLayersDialog from '@components/portal/table/batch-upload-layers-dialog';
import { colors } from '@constants/colors';
import { getDashboardMenuConfig, isNewBatch } from '@constants/config';
import * as dialog from '@constants/dialogs';
import { Button, Icon } from '@mui';
import AddButton from '@shared/add-button';
import BatchUploadForm from '@shared/batch-upload-form';
import ExportButton from '@shared/export-button';
import IconMenu from '@shared/icon-menu';
import IconMenuItem from '@shared/icon-menu-item';
import './header.scss';

const applyLabelPostfix = (label, dataType) => {
  if (dataType === GROUP_DATA_TYPE) {
    return `${label} groups`;
  }
  return label;
};

const getAddTooltip = (dataType, subType) => {
  if (dataType === GROUP_DATA_TYPE) {
    return `Add ${subType} group`;
  }
  return `Add ${subType ? subType : dataType}`;
};

const Header = ({ dataType, subType }) => {
  const dispatch = useDispatch();
  const [batchOpen, setBatchOpen] = useState(false);

  // Get type config for top-bar actions (used by People pages)
  const typeConfig = useSelector(state => getDataTypeConfig(state, dataType, subType));
  const { ['top-bar']: { link, permission } = {} } = typeConfig || {};
  const readOnly = useTypeIsReadOnly(dataType, subType);
  const hasLinkAccess = useCanAccessType(null, dataType, subType, permission);

  // Build label from menu config or type config
  const label = useMemo(() => {
    const menuConfig = getDashboardMenuConfig();
    const menuItems = Array.isArray(menuConfig) ? flattenDeep(menuConfig.map(section => section.items)) : [];
    // First try to find in menu items
    const menuItem = menuItems.find(item => item.type === dataType || item.type === subType);
    if (menuItem) {
      return applyLabelPostfix(menuItem.label, dataType);
    }
    // Fall back to type config
    if (typeConfig?.label) {
      return applyLabelPostfix(typeConfig.label, dataType);
    }
    return dataType;
  }, [dataType, subType, typeConfig]);

  // Get filter params for export
  const filterParams = useSelector(state => getDataTypeFilterParams(state, dataType, subType));

  const openDialog = useCallback(
    () => dispatch(openDashboardDialog(dialog.NAME_DESCRIPTION, { dataType, portal: 'admin' })),
    [dataType, dispatch]
  );

  const onAdd = useCallback(() => {
    if (link) {
      dispatch(push(link.to));
    }
  }, [dispatch, link]);

  const onExport = useCallback(() => {
    dispatch(downloadCSVExport(dataType, subType, filterParams));
  }, [dispatch, dataType, subType, filterParams]);

  // Batch upload handlers
  const openBatch = useCallback(() => setBatchOpen(true), []);
  const closeBatch = useCallback(() => setBatchOpen(false), []);
  const openEntities = useCallback(() => dispatch(openDashboardDialog(dialog.BATCH_ENTITY)), [dispatch]);
  const openLayers = useCallback(() => dispatch(openDashboardDialog(dialog.BATCH_LAYER)), [dispatch]);

  // Determine which action buttons to show
  const showAddButton = link && hasLinkAccess && !readOnly && dataType !== BATCH_DATA_TYPE;
  const showCreateButton = !link && dataType !== BATCH_DATA_TYPE; // Admin portal style (workflow, etc.)
  const isBatch = dataType === BATCH_DATA_TYPE;

  return (
    <div data-testid="header" styleName="header-container">
      <div styleName="title">
        {label}
      </div>
      <div styleName="action">
        {showAddButton && (
          <AddButton onClick={onAdd} tooltip={getAddTooltip(dataType, subType)} />
        )}
        {showAddButton && (
          <ExportButton onClick={onExport} />
        )}
        {showCreateButton && (
          <Button
            plain
            color="primary"
            onClick={openDialog}
            variant="contained"
          >
            CREATE {label}
          </Button>
        )}
        {/* Old batch upload button */}
        {isBatch && !isNewBatch() && (
          <Fragment>
            <AddButton onClick={openBatch} tooltip="Batch upload" />
            <BatchUploadForm open={batchOpen} onClose={closeBatch} />
          </Fragment>
        )}
        {/* New batch upload with menu */}
        {isBatch && isNewBatch() && (
          <Fragment>
            <BatchUploadEntitiesDialog />
            <BatchUploadLayersDialog />
            <IconMenu
              icon={<Icon color={colors.opacity.black0_54} role="img">add</Icon>}
              iconButtonStyles={{}}
              MenuProps={{
                anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                transformOrigin: { horizontal: 'center', vertical: 'top' }
              }}
              tooltip="Batch upload"
            >
              <IconMenuItem icon={<Icon role="img">work</Icon>} onClick={openEntities}>Entities</IconMenuItem>
              <IconMenuItem icon={<Icon role="img">layers</Icon>} onClick={openLayers}>City Layers</IconMenuItem>
            </IconMenu>
          </Fragment>
        )}
        {/* Always show export for batch */}
        {isBatch && <ExportButton onClick={onExport} />}
      </div>
    </div>
  );
};

Header.propTypes = {
  dataType: PropTypes.string,
  subType: PropTypes.string
};

export default Header;
